import { handleEvoWrapperMessages } from '@lobby/oss/utils';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Provider } from 'react-redux';

import { initializeDataDog } from '@ezugi/analytics/initializeDataDog';
import useRotateViewportSizeVariables from '@ezugi/bootstrap/hooks/useRotateViewportSizeVariables';
import { setViewportSizeVariables } from '@ezugi/bootstrap/util/viewport';
import { di } from '@ezugi/constants/device';
import { AppConfig } from '@ezugi/utils/core/appConfig';
import { DeviceInfo } from '@ezugi/utils/helpers/deviceInfo';

import { AppRouter } from './AppRouter';
import { RouteContextProvider } from './contexts/RouteContext';
import configureAppStore from './store';
import { analytics } from './store/analytics';

/* eslint-disable no-undef */
/**
 * Add global variables for app & tag version
 * Replaced by Vite at build time
 */
window.__VERSION = __VERSION;
window.__TAG_VERSION = __TAG_VERSION;
/* eslint-enable no-undef */

const handlePageShow = (event) => {
  const shouldReload = event.persisted
    || performance.getEntriesByType('navigation').at(-1)?.type === 'back_forward';

  if (!shouldReload) return;

  window.location.reload();
};

const getHtmlHeight = () => {
  if (DeviceInfo.isIOS26) {
    return 'calc(100dvh + 1px)';
  }

  return DeviceInfo.isHandheld ? 'var(--innerHeight)' : '100%';
};

const LOBBY_DEVICE_BODY_CLASSES = new Set([ di.ios, di.ios26, di.android, di.desktop ]);

const Lobby = () => {
  const storeRef = useRef(configureAppStore());

  useRotateViewportSizeVariables();

  useEffect(() => {
    const html = document.documentElement;
    const originalHeight = html.style.height;
    const originalTouchAction = html.style.touchAction;

    if (DeviceInfo.isHandheld) {
      setViewportSizeVariables();
    }

    html.style.height = getHtmlHeight();

    if (DeviceInfo.isIOS) {
      html.style.touchAction = 'none';
    }

    return () => {
      html.style.height = originalHeight;
      html.style.touchAction = originalTouchAction;
      html.style.removeProperty('--innerWidth');
      html.style.removeProperty('--innerHeight');
    };
  }, []);

  useLayoutEffect(() => {
    // Resets the scale transform used by games in desktop mode
    const app = document.getElementById('app');
    app?.removeAttribute('style');

    document.body.classList.add(di.lobbyView);
    document.body.classList.toggle(di.ios, DeviceInfo.isIOS);
    document.body.classList.toggle(di.ios26, DeviceInfo.isIOS26);
    document.body.classList.toggle(di.android, DeviceInfo.isAndroid);
    document.body.classList.toggle(di.desktop, !DeviceInfo.isHandheld);

    return () => {
      document.body.classList.remove(di.lobbyView);
      document.body.className = document.body.className
        .split(' ')
        .filter((className) => !LOBBY_DEVICE_BODY_CLASSES.has(className))
        .join(' ');
    };
  }, []);

  useEffect(() => {
    initializeDataDog();

    storeRef.current.dispatch(analytics.requestConfig());

    const handleEvoWrapper = (event) => handleEvoWrapperMessages(event, storeRef.current);

    window.addEventListener('message', handleEvoWrapper);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('message', handleEvoWrapper);
      window.removeEventListener('pageshow', handlePageShow);

      AppConfig.clearInstance();
    };
  }, [ storeRef ]);

  return (
    <React.StrictMode>
      <Provider store={storeRef.current}>
        <RouteContextProvider>
          <AppRouter />
        </RouteContextProvider>
      </Provider>
    </React.StrictMode>
  );
};

export default Lobby;
